import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Circle, ArrowRight, X, Rocket } from 'lucide-react';
import { supabase } from '../lib/supabase';

/**
 * Full-width onboarding banner rendered at the top of /dashboard when
 * `shops.onboarding_completed_at IS NULL`. Complements the compact
 * sidebar checklist so mobile users (whose sidebar collapses to a
 * bottom-nav strip and hides the sidebar chip) still see the prompt.
 *
 * Milestones (identical to the /welcome wizard order, plus M4 hours):
 *   1. Shop profile        — shops.name + subdomain
 *   2. Business hours      — shops.opening_hours JSONB (M4)
 *   3. Add services        — ≥1 services row
 *   4. Connect payments    — shops.stripe_account_id
 *   5. Add a barber        — ≥1 barbers row
 *
 * Each row is directly clickable → jumps to the specific setup page.
 *
 * Dismiss behaviour: session-scoped. `sessionStorage['instacut.ob.dismissed']`
 * hides the banner for the current tab only; it re-appears on next
 * sign-in / new tab / next day.
 */
export const OnboardingBanner = ({ profile }) => {
  const shop = profile?.shops || null;
  const isShopAdmin = profile?.role === 'shop_admin';
  const incomplete = shop && !shop.onboarding_completed_at;

  const [servicesCount, setServicesCount] = useState(0);
  const [barbersCount, setBarbersCount] = useState(0);
  const [dismissed, setDismissed] = useState(
    () => typeof window !== 'undefined' && sessionStorage.getItem('instacut.ob.dismissed') === '1'
  );

  useEffect(() => {
    if (!isShopAdmin || !incomplete || !shop?.id) return;
    (async () => {
      const [svc, brb] = await Promise.all([
        supabase.from('services').select('id', { count: 'exact', head: true }).eq('shop_id', shop.id),
        supabase.from('barbers').select('id',  { count: 'exact', head: true }).eq('shop_id', shop.id),
      ]);
      setServicesCount(svc.count || 0);
      setBarbersCount(brb.count || 0);
    })();
  }, [isShopAdmin, incomplete, shop?.id]);

  if (!isShopAdmin || !incomplete || dismissed) return null;

  const items = [
    { key: 'profile',  label: 'Shop profile',   hint: 'name + subdomain + tagline',                 to: '/profile',  done: !!(shop.name && shop.subdomain) },
    { key: 'hours',    label: 'Business hours', hint: 'when customers can see you\u2019re open',    to: '/profile',  done: Array.isArray(shop.opening_hours) && shop.opening_hours.length > 0 },
    { key: 'services', label: 'Add services',   hint: 'the cuts you offer',                         to: '/services', done: servicesCount > 0 },
    { key: 'payments', label: 'Connect payments', hint: 'so deposits actually land',                to: '/billing',  done: !!shop.stripe_account_id },
    { key: 'barber',   label: 'Add a barber',   hint: 'at least one chair',                         to: '/barbers',  done: barbersCount > 0 },
  ];
  const doneCount = items.filter(i => i.done).length;
  const total = items.length;
  const pct = Math.round((doneCount / total) * 100);

  const dismiss = () => {
    sessionStorage.setItem('instacut.ob.dismissed', '1');
    setDismissed(true);
  };

  return (
    <div
      className="ob-banner"
      data-testid="dashboard-onboarding-banner"
      style={{
        border: '1px solid var(--acc-soft, #e0e7ff)',
        borderLeft: '4px solid var(--acc)',
        background: 'linear-gradient(180deg, rgba(79, 70, 229, 0.05) 0%, transparent 100%)',
        borderRadius: 12,
        padding: 18,
        marginBottom: 20,
        position: 'relative',
      }}
    >
      <button
        type="button"
        className="ob-dismiss"
        onClick={dismiss}
        aria-label="Dismiss for this session"
        data-testid="dashboard-onboarding-dismiss"
        style={{
          position: 'absolute', top: 10, right: 10,
          background: 'transparent', border: 'none',
          padding: 6, borderRadius: 8,
          color: 'var(--mute)', cursor: 'pointer',
        }}
      >
        <X size={16} />
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <div
          style={{
            width: 34, height: 34, borderRadius: 8,
            background: 'var(--acc)',
            color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Rocket size={17} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: '-0.005em' }}>
            Finish setting up your shop
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--mute)', marginTop: 1 }} data-testid="dashboard-onboarding-progress">
            {doneCount} of {total} done · {pct}%
          </div>
        </div>
        <Link
          to="/welcome"
          className="btn p"
          data-testid="dashboard-onboarding-cta"
          style={{
            fontSize: 13, padding: '8px 12px',
            display: 'inline-flex', alignItems: 'center', gap: 4,
            textDecoration: 'none', whiteSpace: 'nowrap',
          }}
        >
          {doneCount === 0 ? 'Start wizard' : 'Continue'} <ArrowRight size={13} />
        </Link>
      </div>

      {/* Progress bar */}
      <div
        style={{
          height: 4, borderRadius: 2, background: 'var(--line2, #f1f5f9)',
          marginTop: 12, marginBottom: 12, overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`, height: '100%',
            background: 'var(--acc)',
            transition: 'width 0.3s ease',
          }}
        />
      </div>

      {/* Milestone list */}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((item) => (
          <li key={item.key} style={{ marginBottom: 2 }}>
            <Link
              to={item.to}
              data-testid={`dashboard-onboarding-item-${item.key}`}
              data-done={item.done ? 'true' : 'false'}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 6px',
                borderRadius: 6,
                color: item.done ? 'var(--mute)' : 'var(--ink)',
                textDecoration: 'none',
                fontSize: 13.5,
              }}
              onMouseEnter={(e) => { if (!item.done) e.currentTarget.style.background = 'var(--line2, #f1f5f9)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {item.done
                ? <Check size={15} stroke="#10b981" strokeWidth={3} style={{ flexShrink: 0 }} />
                : <Circle size={15} stroke="var(--mute)" strokeWidth={2} style={{ flexShrink: 0 }} />}
              <span style={{
                textDecoration: item.done ? 'line-through' : 'none',
                fontWeight: item.done ? 400 : 600,
              }}>
                {item.label}
              </span>
              <span style={{ fontSize: 11.5, color: 'var(--mute)', fontWeight: 400 }}>· {item.hint}</span>
              {!item.done && (
                <ArrowRight size={12} style={{ marginLeft: 'auto', color: 'var(--acc)', flexShrink: 0 }} />
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default OnboardingBanner;
